import { useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { isAxiosError } from 'axios';

import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import {
  useDeleteDesktopNotification,
  useDesktopNotifications,
  useUpdateDesktopNotification,
} from '@/queries/useDesktopNotifications';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { desktopNotificationApi } from '@/api/desktopNotificationApi';
import {
  DESKTOP_NOTIFICATION_TYPE_LABELS,
  type DesktopNotificationListItemDto,
} from '@/api/types/desktopNotification.types';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/dateFormatter';

type EnabledFilter = 'all' | 'enabled' | 'disabled';

function getEnabledParam(filter: EnabledFilter): boolean | undefined {
  if (filter === 'enabled') return true;
  if (filter === 'disabled') return false;
  return undefined;
}

export function DesktopNotificationsPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Admin']);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('all');
  const {
    data: notifications,
    isLoading,
    isError,
  } = useDesktopNotifications(getEnabledParam(enabledFilter));

  const updateMutation = useUpdateDesktopNotification();
  const deleteMutation = useDeleteDesktopNotification();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<DesktopNotificationListItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DesktopNotificationListItemDto | null>(
    null,
  );

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const detailPath = (id: number) =>
    paths.admin.desktopNotificationDetail.replace(':id', String(id));

  const handleFilterChange = (e: SelectChangeEvent<EnabledFilter>) => {
    setEnabledFilter(e.target.value as EnabledFilter);
  };

  const handleCreate = () => {
    navigate(detailPath(0));
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: DesktopNotificationListItemDto,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuItem(item);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuItem(null);
  };

  const handleEdit = () => {
    if (menuItem) {
      navigate(detailPath(menuItem.id));
    }
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (menuItem) {
      setDeleteTarget(menuItem);
    }
    handleCloseMenu();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        rowVersion: deleteTarget.rowVersion,
      });
      showNotification('Reminder deleted.', 'success');
    } catch {
      showNotification('Failed to delete reminder.', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleEnabled = async (item: DesktopNotificationListItemDto) => {
    try {
      const detail = await desktopNotificationApi.getById(item.id);
      await updateMutation.mutateAsync({
        id: item.id,
        request: {
          name: detail.name,
          enabled: !item.enabled,
          dismissEnabled: detail.dismissEnabled,
          schedules: detail.schedules,
          timeZone: detail.timeZone,
          header: detail.header,
          body: detail.body,
          audience: detail.audience,
          rowVersion: detail.rowVersion,
        },
      });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        return;
      }
      showNotification('Failed to update reminder status.', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Desktop Notifications Admin
      </Typography>

      <Section>
        <SectionHeader>REMINDERS</SectionHeader>
        <SectionCard>
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="enabled-filter-label">Enabled</InputLabel>
              <Select
                labelId="enabled-filter-label"
                label="Enabled"
                value={enabledFilter}
                onChange={handleFilterChange}
                inputProps={{ 'aria-label': 'Filter by enabled' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" color="primary" onClick={handleCreate}>
              Create reminder
            </Button>
          </Stack>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error" role="alert">
              Failed to load reminders.
            </Alert>
          ) : !notifications || notifications.length === 0 ? (
            <EmptyStateMessage message="No reminders yet. Click 'Create reminder' to add one." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Audience</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Dismiss</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <MuiLink component={RouterLink} to={detailPath(item.id)}>
                          {item.name}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{DESKTOP_NOTIFICATION_TYPE_LABELS[item.type]}</TableCell>
                      <TableCell>{item.scheduleSummary}</TableCell>
                      <TableCell>{item.audienceSummary}</TableCell>
                      <TableCell>
                        <Switch
                          checked={item.enabled}
                          onChange={() => handleToggleEnabled(item)}
                          slotProps={{ input: { 'aria-label': `Toggle ${item.name}` } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant={item.dismissEnabled ? 'outlined' : 'filled'}
                          color={item.dismissEnabled ? 'default' : 'warning'}
                          label={item.dismissEnabled ? 'On' : 'Off'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(item.updatedAt)}</TableCell>
                      <TableCell>
                        <Tooltip title="Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, item)}
                            aria-label={`Actions for ${item.name}`}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Section>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        aria-labelledby="delete-reminder-title"
      >
        <DialogTitle id="delete-reminder-title">Delete reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DesktopNotificationsPage;
